import { EnterSearchInput } from "../../../components/ui/EnterSearchInput";
import { TablePagination } from "../../../components/ui/TablePagination";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { FormSelect } from "../../../components/ui/select";
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, AlertTriangle } from 'lucide-react';
import i18n from '../../../i18n';
import { Trans } from 'react-i18next';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  loadProductCatalog,
  fetchProductCount,
  fetchProductListCategoryFilterOptions,
} from '../../../services/productCatalogService';

const TOAST_KEY = 'product_catalog_toast';
/** Matches GET /product-catalog/search?page=0&size=10&sortBy=productName&sortDir=ASC */
const PAGE_SIZE = 10;

function translateProductStatus(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  if (status === 'End of Production') return i18n.t('inventory.endOfProduction');
  return status;
}

function ProductStatusBadge({ status }) {
  const isEndOfProd = status === 'End of Production';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{
        backgroundColor: status === 'Active' ? '#ecfdf5' : isEndOfProd ? '#fef3c7' : '#f1f5f9',
        color: status === 'Active' ? '#065f46' : isEndOfProd ? '#b45309' : '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {translateProductStatus(status)}
    </span>
  );
}

function ProductMobileCard({ prod, onSelect, onEdit }) {
  const isEndOfProd = prod.status === 'End of Production';

  return (
    <div
      className="p-4 space-y-3 active:bg-workspace/40 transition cursor-pointer"
      style={{ backgroundColor: isEndOfProd ? '#fffbeb' : undefined }}
      onClick={() => onSelect(prod)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(prod);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex justify-between items-start gap-2">
        <h4 className="font-semibold text-foreground leading-snug">{prod.name}</h4>
        <ProductStatusBadge status={prod.status} />
      </div>

      <div className="text-xs space-y-1 per-file-hint">
        <div className="flex justify-between gap-3">
          <span>{i18n.t('inventory.partNumber')}:</span>
          <span className="font-medium text-foreground font-mono text-right">{prod.partNumber}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span>{i18n.t('common.category')}:</span>
          <span className="font-medium text-foreground text-right">{prod.category}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span>{i18n.t('crm.manufacturer')}:</span>
          <span className="font-medium text-foreground text-right">{prod.manufacturer}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span>{i18n.t('inventory.availableStock')}:</span>
          <span
            className="font-semibold text-right"
            style={{ color: prod.availableStock === 0 ? 'var(--accent-red, #ef4444)' : 'inherit' }}
          >
            {prod.availableStock}
          </span>
        </div>
        {isEndOfProd && (
          <div className="flex items-center gap-1 text-[11px] font-bold text-[#b45309] pt-1">
            <AlertTriangle size={12} />
            <Trans i18nKey="inventory.endOfProductionReplacementsNeeded" />
          </div>
        )}
      </div>

      <div className="flex justify-between items-center pt-2" onClick={(e) => e.stopPropagation()}>
        <span className="text-[11px] font-semibold per-file-hint uppercase tracking-wide">
          <Trans i18nKey="common.actions" />
        </span>
        <button
          type="button"
          className="text-[13px] font-semibold text-primary cursor-pointer bg-transparent border-0 p-0"
          onClick={() => onEdit(prod)}
        >
          <Trans i18nKey="common.edit" />
        </button>
      </div>
    </div>
  );
}

export default function ProductCatalog() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterManufacturer, setFilterManufacturer] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryOptionsLoaded, setCategoryOptionsLoaded] = useState(false);

  // Category filter options via GET /product-catalog/search/service/{productServiceId}
  useEffect(() => {
    if (!showFilters || categoryOptionsLoaded) return undefined;
    let cancelled = false;
    fetchProductListCategoryFilterOptions({ size: 10 })
      .then((options) => {
        if (!cancelled) {
          setCategoryOptions(options || []);
          setCategoryOptionsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryOptions([]);
          setCategoryOptionsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showFilters, categoryOptionsLoaded]);

  const manufacturers = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.manufacturer) set.add(String(p.manufacturer).trim());
    });
    if (filterManufacturer !== 'ALL' && filterManufacturer) set.add(filterManufacturer);
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [products, filterManufacturer]);

  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [filterCategory, filterStatus, filterManufacturer]);

  // Table list uses the documented search endpoints when a query is present.
  useEffect(() => {
    let cancelled = false;
    if (searchQuery.trim() !== debouncedSearch) {
      setLoading(true);
      return () => { cancelled = true; };
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await loadProductCatalog({
          searchQuery: debouncedSearch,
          filterCategory,
          filterProductServiceId: filterCategory,
          filterStatus,
          filterManufacturer,
          page,
          size: PAGE_SIZE,
        });
        if (cancelled) return;
        const list = result.products || [];
        setProducts(list);
        const elements = result.totalElements ?? list.length;
        setTotalElements(elements);
        setTotalPages(
          result.totalPages ?? Math.max(1, Math.ceil(elements / PAGE_SIZE))
        );
      } 
      catch (err) {
        if (cancelled) return;
        setProducts([]);
        setTotalElements(0);
        setTotalPages(0);
        setError(err?.message || i18n.t('inventory.failedToLoadProducts'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [searchQuery, debouncedSearch, filterCategory, filterStatus, filterManufacturer, page]);

  // Global count — once on mount (not tied to products[] state).
  useEffect(() => {
    let cancelled = false;
    fetchProductCount()
      .then((count) => {
        if (!cancelled) setTotalCount(count);
      })
      .catch(() => {
        if (!cancelled) setTotalCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadProducts = () => {
    setLoading(true);
    setError(null);
    loadProductCatalog({
      searchQuery: debouncedSearch,
      filterCategory,
      filterProductServiceId: filterCategory,
      filterStatus,
      filterManufacturer,
      page,
      size: PAGE_SIZE,
    })
      .then((result) => {
        const list = result.products || [];
        setProducts(list);
        const elements = result.totalElements ?? list.length;
        setTotalElements(elements);
        setTotalPages(
          result.totalPages ?? Math.max(1, Math.ceil(elements / PAGE_SIZE))
        );
      })
      .catch((err) => {
        setProducts([]);
        setTotalElements(0);
        setTotalPages(0);
        setError(err?.message || i18n.t('inventory.failedToLoadProducts'));
      })
      .finally(() => setLoading(false));
  };

  const onAddProduct = () => navigate('/IMS/inventory/products/new');
  const onEditProduct = (prod) => navigate(`/IMS/inventory/products/${prod.id}/edit`);
  const onRowClick = (prod) => navigate(`/IMS/inventory/products/${prod.id}`);
  const onResetFilters = () => {
    setFilterCategory('ALL');
    setFilterManufacturer('ALL');
    setFilterStatus('ALL');
    setSearchQuery('');
    setPage(0);
  };

  const isFiltersActive =
    filterCategory !== 'ALL' ||
    filterManufacturer !== 'ALL' ||
    filterStatus !== 'ALL' ||
    searchQuery !== '';

  const handleClearFiltersClick = () => {
    onResetFilters();
  };

  const hasNoProductsGlobal =
    !loading &&
    !error &&
    products.length === 0 &&
    !isFiltersActive &&
    page === 0 &&
    (totalCount === 0 || totalCount === null);

  // Only show "No Match" when the user actually filtered/searched.
  const hasNoMatch =
    !loading && !error && products.length === 0 && isFiltersActive;

  // Search returned nothing while count says products exist (and no filters).
  const hasListLoadIssue =
    !loading &&
    !error &&
    products.length === 0 &&
    !isFiltersActive &&
    totalCount != null &&
    totalCount > 0;

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <div className="inventory-module">
    <div style={{ padding: '0 8px' }}>
      {/* PAGE HEADER */}
      <div className="page-header-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <PageTitleRow back={<PageBackLink to="/" />}>
            <h1 className="page-title"><Trans i18nKey="inventory.productCatalogTitle" /></h1>
          </PageTitleRow>
        </div>
        <button 
          type="button" 
          className="btn-primary" 
          onClick={onAddProduct}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' }}
        >
          <Plus size={18} />
          <span><Trans i18nKey="formActions.addProduct" /></span>
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '14px 16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{error}</span>
          <button type="button" className="btn-secondary" style={{ height: '32px', fontSize: '13px' }} onClick={reloadProducts}>
            <Trans i18nKey="attendance.retry" />
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '48px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{ height: '44px', borderRadius: '8px', backgroundColor: '#f1f5f9', animation: 'pulse 1.5s ease-in-out infinite' }} />
            ))}
          </div>
        </div>
      ) : hasNoProductsGlobal ? (
        /* EDGE CASE: NO PRODUCTS EXIST YET */
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#f0fdf4', color: 'var(--accent-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
            <Plus size={32} />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-navy)', marginBottom: '8px', fontFamily: 'var(--font-display)' }}><Trans i18nKey="inventory.noProductsCreatedYet" /></h3>
          <p style={{ color: 'var(--text-grey)', fontSize: '14px', maxWidth: '400px', marginBottom: '24px', lineHeight: '1.5' }}>
            <Trans i18nKey="inventory.noProductsCreatedHint" />
          </p>
          <button 
            type="button" 
            className="btn-primary" 
            onClick={onAddProduct}
            style={{ height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' }}
          >
            <Trans i18nKey="inventory.createProduct" />
          </button>
        </div>
      ) : hasListLoadIssue ? (
        <div style={{ padding: '40px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary-navy)', marginBottom: '8px' }}>
            <Trans i18nKey="inventory.productsCouldNotBeLoaded" />
          </h3>
          <p style={{ color: 'var(--text-grey)', fontSize: '13px', marginBottom: '16px' }}>
            {i18n.t('inventory.catalogCountMismatchHint', { totalCount })}
          </p>
          <button type="button" className="btn-secondary" onClick={reloadProducts} style={{ height: '36px' }}>
            <Trans i18nKey="attendance.retry" />
          </button>
        </div>
      ) : (
        <>
          {/* SEARCH & FILTERS BAR */}
          <div className="filters-card-clean inv-filter-panel" style={{ marginBottom: '8px', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
            <div className="filter-search-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', width: '100%' }}>
              <div className="filter-search-wrapper" style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '360px' }}>
                <Search className="filter-search-icon" size={16} />
                <EnterSearchInput data-page-search="true"
                  type="text" 
                  className="filter-search-input" 
                  placeholder={i18n.t('inventory.searchByProductNameOrPartNumber')} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '40px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: showFilters || isFiltersActive ? '#eff6ff' : '#ffffff',
                  color: showFilters || isFiltersActive ? 'var(--accent-blue)' : 'var(--text-dark)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'var(--transition-all)'
                }}
              >
                <Filter size={16} />
                <span><Trans i18nKey="formActions.filter" /></span>
                {isFiltersActive && (
                  <span style={{
                    backgroundColor: 'var(--accent-blue)',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '2px'
                  }}>
                    !
                  </span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="inv-filter-fields" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', width: '100%', paddingTop: '4px' }}>
                <FormSelect
                  className="filter-select" 
                  value={filterCategory} 
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="ALL">
                    {categoryOptionsLoaded || categoryOptions.length > 0
                      ? i18n.t('inventory.allCategories')
                      : i18n.t('inventory.loadingCategories')}
                  </option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </FormSelect>

                <FormSelect
                  className="filter-select" 
                  value={filterManufacturer} 
                  onChange={(e) => setFilterManufacturer(e.target.value)}
                >
                  <option value="ALL"><Trans i18nKey="inventory.allManufacturers" /></option>
                  {manufacturers.map((mfg) => (
                    <option key={mfg} value={mfg}>{mfg}</option>
                  ))}
                </FormSelect>

                <FormSelect
                  className="filter-select" 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="ALL"><Trans i18nKey="common.allStatuses" /></option>
                  <option value="Active">{i18n.t('status.active')}</option>
                  <option value="Inactive">{i18n.t('status.inactive')}</option>
                  <option value="End of Production">{i18n.t('inventory.endOfProduction')}</option>
                </FormSelect>

                {isFiltersActive && (
                  <button 
                    type="button" 
                    className="btn-clear-filters" 
                    onClick={handleClearFiltersClick}
                  >
                    <Trans i18nKey="formActions.clearFilters" />
                  </button>
                )}
              </div>
            )}
          </div>

          {hasNoMatch ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: 'var(--text-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                <Search size={24} />
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary-navy)', marginBottom: '6px' }}><Trans i18nKey="inventory.noMatchFound" /></h3>
              <p style={{ color: 'var(--text-grey)', fontSize: '13px', maxWidth: '360px', marginBottom: '16px', lineHeight: '1.45' }}>
                <Trans i18nKey="inventory.noProductsMatchFilters" />
              </p>
              <button 
                type="button" 
                className="btn-secondary" 
                style={{ height: '36px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}
                onClick={handleClearFiltersClick}
              >
                <Trans i18nKey="inventory.resetSearchFilters" />
              </button>
            </div>
          ) : (
            <div className="table-section borderless-table-section">
              <div className="table-wrapper hidden md:block">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th><Trans i18nKey="inventory.productName" /></th>
                      <th><Trans i18nKey="inventory.partNumber" /></th>
                      <th><Trans i18nKey="common.category" /></th>
                      <th><Trans i18nKey="crm.manufacturer" /></th>
                      <th><Trans i18nKey="status.status" /></th>
                      <th><Trans i18nKey="inventory.availableStock" /></th>
                      <th style={{ width: '120px', textAlign: 'right' }}><Trans i18nKey="common.actions" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((prod) => {
                      const isEndOfProd = prod.status === 'End of Production';
                      
                      return (
                        <tr 
                          key={prod.id} 
                          className="clickable-row"
                          onClick={() => onRowClick(prod)}
                          style={{
                            backgroundColor: isEndOfProd ? '#fffbeb' : 'transparent',
                            transition: 'var(--transition-all)'
                          }}
                        >
                          <td style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span>{prod.name}</span>
                              {isEndOfProd && (
                                <span style={{ fontSize: '11px', color: '#b45309', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <AlertTriangle size={12} />
                                  <Trans i18nKey="inventory.endOfProductionReplacementsNeeded" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-grey)' }}>
                            {prod.partNumber}
                          </td>
                          <td>
                            <span className="badge-wrapper" style={{ fontSize: '13px', color: 'var(--text-dark)' }}>
                              {prod.category || i18n.t('inventory.emDash')}
                            </span>
                          </td>
                          <td>{prod.manufacturer}</td>
                          <td>
                            <span 
                              style={{ 
                                display: 'inline-flex', 
                                padding: '4px 10px', 
                                borderRadius: '12px', 
                                fontSize: '11px', 
                                fontWeight: '700', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                backgroundColor: prod.status === 'Active' ? '#ecfdf5' : isEndOfProd ? '#fef3c7' : '#f1f5f9',
                                color: prod.status === 'Active' ? '#065f46' : isEndOfProd ? '#b45309' : '#475569'
                              }}
                            >
                              {translateProductStatus(prod.status)}
                            </span>
                          </td>
                          <td style={{ fontWeight: '700' }}>
                            <span style={{ color: prod.availableStock === 0 ? 'var(--accent-red)' : 'inherit' }}>
                              {prod.availableStock}
                            </span>
                          </td>
                           <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <span
                                  style={{
                                  color: "var(--accent-blue)",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                e.stopPropagation();
                                onEditProduct(prod);
                              }}
                              >
                              <Trans i18nKey="common.edit" />
                            </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-(--color-text-border)/35">
                {products.map((prod) => (
                  <ProductMobileCard
                    key={prod.id}
                    prod={prod}
                    onSelect={onRowClick}
                    onEdit={onEditProduct}
                  />
                ))}
              </div>

              <TablePagination page={page + 1} totalPages={totalPages} totalItems={totalElements} pageSize={PAGE_SIZE} onPageChange={(next) => setPage(next - 1)}  />
            </div>
          )}
        </>
      )}
    </div>
      </div>
    </AppLayout>
  );
}
